/*
 * Copyright (C) 2019 - present Marek Kuzora - All Rights Reserved.
 */


import EntityEnum from 'galax/enum/entity';
import ParentEnum from 'galax/enum/parent';

import Cache from 'fierry/flux/cache';
import Model from 'fierry/rpc.old/model';

import PlanetAttributes from 'galax/server/planet/attributes';
import PlanetInstaller from 'galax/server/planet/installer';

import EntityCollection from 'galax/server/entity/collection';
import ZoneCollection from 'galax/server/zone/collection';
import SectorCollection from 'galax/server/sector/collection';

import InteractionCollection from 'galax/server/interaction/collection';
import ProjectileCollection from 'galax/server/projectile/collection';
import LocalUserCollection from 'galax/server/user/local.collection';

import Dock from 'galax/server/dock';

import PlanetGeometry from 'galax/client/geometry/planet';

import { makeLocation } from 'galax/server/location';

import * as Coords from 'shared/geo/coords';
import * as Shapes from 'shared/geo/shapes';
import * as Array from 'fierry/util/array';


export default class extends Model
{
  constructor (state, hierarchy)
  {
    super(state, hierarchy);

    this.attributes = new PlanetAttributes(this);

    this.interactions = new InteractionCollection(state);
    this.projectiles = new ProjectileCollection(this, state);

    this.users = new LocalUserCollection(this, state.get('users'));
    this.sectors = new SectorCollection(this, state.get('sectors'));
    this.entities = new EntityCollection(this, state.get('entities'));

    this.zones = new ZoneCollection(this, state.get('zones')); // TODO needs to be here since zone doesn't know its user without sector!

    this.__createDocks__();

    this.install();

    this.geometryCache = new Cache(zoom => new PlanetGeometry(zoom));
  }


  __createDocks__ ()
  {
    this.docks = new Map();

    for (const zone of this.getZones())
    {
      for (const coords of Coords.getNeighborsInRange(zone.getCoords()))
      {
        if (this.getZone(coords) == null && !this.docks.has(coords.toString()))
        {
          this.docks.set(coords.toString(), new Dock(coords, this))
        }
      }
    }
  }


  install ()
  {
    const installer = new PlanetInstaller(this);

    this.zones.setupInstaller(installer);
    this.sectors.setupInstaller(installer);
    this.entities.setupInstaller(installer);
    this.interactions.setupInstaller(installer);

    installer.execute();
  }


  getAttributes ()
  {
    return this.attributes;
  }


  getInteractions ()
  {
    return this.interactions;
  }


  getProjectiles ()
  {
    return this.projectiles;
  }


  getEntities ()
  {
    return this.entities;
  }


  getZoneCollection ()
  {
    return this.zones;
  }


  getBuilding (zoneID)
  {
    return this.entities.getBuilding(zoneID);
  }


  getUnit (coords)
  {
    return this.entities.getUnit(coords.toString());
  }


  getUser (id)
  {
    return this.users.getUser(id);
  }


  getZone (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.getZoneCollection().getZone(coords);
  }


  getDock (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.docks.get(coords.toString());
  }


  getPlatform (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.getZoneCollection().getPlatform(coords);
  }


  getLocation (coordsOrLocation)
  {
    return makeLocation(coordsOrLocation, this);
  }


  getZones ()
  {
    return this.zones.getZones();
  }


  // TODO used by planet/attributes
  eachZone (center, shape, callback)
  {
    for (const coordinate of Shapes.getCoords(center.id, shape))
    {
      callback(this.getZone(coordinate));
    }
  }


  isWithinBounds (coords, delta = 0)
  {
    const { id, x, y, width, height } = this.getCass();

    return (x - delta) <= coords.getX() && coords.getX() <= (x + width + delta) && (y - delta) <= coords.getY() && coords.getY() <= (y + height + delta);
  }


  getSectors ()
  {
    return this.sectors;
  }


  getConstants ()
  {
    return this.state.get('constants');
  }


  getCass ()
  {
    return this.state.get('currentPlanet');
  }


  getID ()
  {
    return this.getCass().id;
  }


  getCoords ()
  {
    return this.getCass().coords;
  }


  isLocal ()
  {
    return true;
  }


  getGeometry (zoom)
  {
    return this.geometryCache.get(zoom);
  }


}
